import { useCallback, useState } from 'react';

/**
 * Tiny `useState`-shaped hook backed by `localStorage`. Used by the
 * Notes redesign to persist per-page layout + group-by preferences
 * (Q3, Q4) without standing up a real preferences endpoint.
 *
 * Defensive against:
 *  - SSR / pre-mount access (returns default if `window` undefined)
 *  - Quota exceeded / private browsing (try/catch around set)
 *  - Bad JSON in storage (catch + return default)
 *
 * The setter has the same `(value) => void` shape as `useState`'s
 * setter — consumers can `setLayout('rows')` directly.
 */
export const useLocalPref = <T>(
  key: string,
  defaultValue: T,
  isValid: (raw: unknown) => raw is T = (raw): raw is T => raw !== undefined,
): [T, (next: T) => void] => {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return defaultValue;
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return defaultValue;
      const parsed = JSON.parse(raw);
      return isValid(parsed) ? parsed : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem(key, JSON.stringify(next));
        }
      } catch {
        // QuotaExceeded / SecurityError — silently ignore. The in-
        // memory state is already updated; user just doesn't get
        // persistence.
      }
    },
    [key],
  );

  return [value, set];
};
